import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Html } from '@react-three/drei';
import axios from 'axios';
import { API } from '../../config/api';
// One bobbin under several products. Same rule Project Token State and the
// machine-queue table use, so the three cannot disagree about which drums are
// the same physical object.
import {
  buildSharedTokenIndex,
  sharedPartners,
  sharedShareIndex,
  buildMergeGroups
} from '../../helpers/sharedTokens';
import ProductAllocationButton from '../../components/project-management/ProductAllocationButton';

const NextPhaseWizzard = lazy(() => import('../production-management/NextPhaseWizzard'));

const http = axios.create({ baseURL: API.REDIS, timeout: 10000 });
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

const httpBackend = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
httpBackend.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

const PHASE_LABEL = {
  unwinding: 'Unwind', pre_stranding: 'Pre-Strand',
  conductor_isolation: 'Isolation', rope: 'Rope',
  interior_sheat: 'Inner Sheath', exterior_sheat: 'Outer Sheath',
  armouring: 'Armouring', rewinding: 'Rewind', coiling: 'Coil',
  isolated_coils: 'Isolated → Coils', cable_on_drum: 'Cable → Coils'
};

const ISOLATION_PHASES = ['conductor_isolation', 'unwinding'];
// Phases that belong in Hall 3 (coiling machines)
const HALL3_PHASES = ['coiling', 'rewinding'];
// Tokens with nextPhase 'armouring' go to Hall 4 — detected via API
const ARMOURING_PHASE = 'armouring';

const STATUS_COLORS = {
  DONE: '#22C55E', FINISHED: '#22C55E', COMPLETED: '#22C55E',
  CONSUMING: '#3B82F6', RESERVED: '#F59E0B',
  PENDING: '#6B7280', PHANTOM: '#8B5CF6',
  CONSUMED: '#6B7280'
};

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32',
  GREY: '#808080', BLACK: '#333', WHITE: '#EEE', RED: '#EF4444',
  YELLOW: '#F59E0B', GREEN: '#22C55E', ORANGE: '#F97316'
};

// ─── DRUM APPEARANCE ───────────────────────────────────────
// Wound-material colour depends on the manufacturing PHASE; the drum flange
// colour depends on the physical drum MATERIAL (wood vs metal).
const COPPER_HEX = '#B87333';        // bare copper — pre-strand / unwinding
const SHEATH_WHITE_HEX = '#F2F2F2';  // inner sheath — interior mantle
const FLANGE_WOOD = '#8B6914';       // wooden drum (default look, unchanged)
const FLANGE_METAL = '#2F80ED';      // metal drum → blue flange

function cableColorFor(phase, tokColor) {
  if (phase === 'unwinding' || phase === 'pre_stranding') return COPPER_HEX;
  if (phase === 'interior_sheat') return SHEATH_WHITE_HEX;
  return COLOR_HEX[tokColor] || '#333';
}

const isMetalDrum = (material) => String(material || '').toLowerCase() === 'metal';

// STOCK vs ORDER(customer) destination for a group of drums (all one job, so
// they share a destination). Phantom / stock-source tokens carry no order
// destination and are, by nature, stock.
function destinationInfo(group) {
  const toks = allTokensOf(group);
  const dest = toks.find(t => t && t.destination)?.destination;
  if (!dest) {
    const stockOnly = toks.some(t => t && (t.isPhantom || t.isPhantomSource));
    return stockOnly ? { kind: 'STOCK', clients: [] } : null;
  }
  const clients = [...new Set((dest.clients || [])
    .map(c => (typeof c === 'string' ? c : c?.name || ''))
    .filter(Boolean))];
  return { kind: dest.kind || (clients.length ? 'ORDER' : 'STOCK'), clients };
}

/**
 * How this group's lot divides between orders and stock.
 *
 * Read off the destination the tokens already carry — the same launch split the
 * tables use, so a drum reads the same here as it does in a list.
 */
function lotSplit(group) {
  // allTokensOf, not group.tokens: a group whose only drum is shared has it
  // drawn under the other product, and reading an empty list would lose the
  // destination entirely.
  const dest = allTokensOf(group).find(t => t && t.destination)?.destination;
  if (!dest) return null;
  const total = Number(dest.lengthTotal) || 0;
  if (total <= 0) return null;
  const toOrders = Math.min(total, Math.max(0, Number(dest.lengthToOrders) || 0));
  return {
    total,
    toOrders,
    toStock: Math.max(0, total - toOrders),
    pct: Math.round((toOrders / total) * 100)
  };
}

/** Units this product has promised to delivery items, across its orders. */
function promisedUnits(group) {
  // allTokensOf, not group.tokens: a group whose only drum is shared has it
  // drawn under the other product, and reading an empty list would lose the
  // destination entirely.
  const dest = allTokensOf(group).find(t => t && t.destination)?.destination;
  return Number(dest?.reservedUnits) || 0;
}

export function useBobinsState() {
  return useQuery({
    queryKey: ['projects', 'bobins-state-3d'],
    queryFn: async () => {
      const res = await http.get('/api/redis/physical-tokens');
      return res.data;
    },
    refetchInterval: 15000,
    staleTime: 10000
  });
}

// Which production hall a token/phase lives in. Mirrors the routing used by the
// hall layouts below: nextPhase armouring → Hall 4, nextPhase coiling/rewinding →
// Hall 3, everything else → Hall 1. Used by Factory3DPage to deep-link to a token.
export function hallForToken(t) {
  const np = t?.nextPhaseName;
  if (np === ARMOURING_PHASE) return 'hall-4';
  if (np && HALL3_PHASES.includes(np)) return 'hall-3';
  return 'hall-1';
}

export function hallForPhase(phase) {
  const p = String(phase || '').toLowerCase();
  if (p === ARMOURING_PHASE) return 'hall-4';
  if (HALL3_PHASES.includes(p)) return 'hall-3';
  return 'hall-1';
}

// ─── FOCUS BEACON — highlight a single deep-linked token ───
// A tall pulsing marker planted on the drum the user clicked in Project Bobin
// State, so it's unmistakable among dozens of drums.
function FocusBeacon({ position, size = 1.2, label = '★ THIS TOKEN' }) {
  const r = size / 2;
  return (
    <group position={position}>
      {/* floor ring around the drum */}
      <mesh position={[0, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[r * 1.4, 0.06, 12, 40]} />
        <meshBasicMaterial color="#22C55E" transparent opacity={0.9} />
      </mesh>
      {/* light pillar */}
      <mesh position={[0, 3.4, 0]}>
        <cylinderGeometry args={[0.05, 0.18, 6.2, 12]} />
        <meshBasicMaterial color="#22C55E" transparent opacity={0.3} />
      </mesh>
      {/* pin head */}
      <mesh position={[0, 6.6, 0]}>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshBasicMaterial color="#22C55E" />
      </mesh>
      <Html position={[0, 7.15, 0]} center>
        <div style={{
          background: '#22C55E', color: '#04210F', padding: '2px 9px',
          borderRadius: 5, fontSize: 9, fontWeight: 800, fontFamily: 'monospace',
          whiteSpace: 'nowrap', userSelect: 'none', pointerEvents: 'none',
          boxShadow: '0 0 14px rgba(34,197,94,0.85)', animation: 'beacon 1.3s infinite'
        }}>
          {label}
        </div>
        <style>{`@keyframes beacon{0%,100%{opacity:1;transform:translateY(0)}50%{opacity:0.55;transform:translateY(-3px)}}`}</style>
      </Html>
    </group>
  );
}

// Lighter marker for EVERY drum of a highlighted product group (dense isolation /
// unwinding zones) — a floor ring + short glow, no tall pillar or label, so the
// whole group lights up without 70 pillars fighting each other.
/**
 * One bobbin, several products.
 *
 * A merge-execution or a partial fork leaves the same drum under two jobs, and
 * it is drawn ONCE — in the first product's group — so the hall shows as many
 * drums as the factory actually has. This is what says the one you are looking
 * at also belongs to somebody else, and names them.
 *
 * Amber, and a `⇄`, because that is the same language Project Token State and
 * the machine queue use for the same fact.
 */
/**
 * Every drum a product holds, including the shared ones drawn under another.
 *
 * `group.tokens` is what gets DRAWN — one mesh each, so the hall shows as many
 * drums as exist. Anything counted, listed or planned must use this instead, or
 * a product that shares a bobbin silently reports fewer metres than it has.
 */
const allTokensOf = group => [
  ...(group?.tokens || []),
  ...(group?.alsoHolds || [])
];

/**
 * A phantom is stock the factory already had — pre-made cable declared into the
 * system so a job can eat it — not something this job produced.
 *
 * It sits in the hall next to real drums and, until now, read exactly like one:
 * a product name and a STOCK badge, which is also what a genuinely produced
 * drum that nobody has ordered looks like. The two are different facts. One came
 * off a machine and has a run behind it; the other was typed in, has no
 * execution, no operator and no metres recorded anywhere.
 */
const isPhantomToken = t => !!(t && (t.isPhantom || t.isPhantomSource));

// The violet the panel already prints the PHANTOM status in — one colour for one
// fact, rather than a second shade that would read as a different thing.
const PHANTOM_HEX = STATUS_COLORS.PHANTOM;

/** Same vocabulary as SharedMarker: a ring on the floor and a small badge. */
function PhantomMarker({ position, size = 1.2 }) {
  const r = size / 2;
  return (
    <group position={position}>
      <mesh position={[0, 0.04, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[r * 1.75, 0.04, 10, 32]} />
        <meshBasicMaterial color={PHANTOM_HEX} transparent opacity={0.85} />
      </mesh>
      <Html position={[0, r + 1.35, 0]} center distanceFactor={22}>
        <div
          title="Phantom — stock declared into the system, not produced on a machine. It has no run, no operator and no recorded metres behind it."
          style={{
            background: 'rgba(139,92,246,0.18)',
            border: `1px solid ${PHANTOM_HEX}`,
            color: PHANTOM_HEX,
            borderRadius: 4,
            padding: '0 4px',
            fontSize: 9,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            pointerEvents: 'auto',
            cursor: 'help'
          }}
        >
          ◇ PHANTOM
        </div>
      </Html>
    </group>
  );
}

/**
 * One bobbin, several products — drawn once, and labelled once.
 *
 * This used to be a bare `⇄ +1` chip, while each sharing product ALSO got its
 * own floating GroupLabel. A product whose every drum is shared draws no drums
 * of its own, so its label never advanced the slot counter and landed on top of
 * the neighbour it was sharing with: two cards at one bobbin, one above and one
 * beside, with nothing saying they were the same object.
 *
 * So the merge states itself here, on the drum, and the products that hold no
 * drum of their own no longer float a card at a slot they do not occupy. The
 * metres are the point — 2 000 m of one product and 5 300 m of another on one
 * bobbin is a merge run; the same two names without the split reads as a
 * duplicate.
 */
function MergeCard({ position, shares = [], partners = [], group, onClickGroup, lift = 2.2, size = 1.2 }) {
  const r = size / 2;
  const rows = shares.length
    ? shares
    : partners.map(label => ({ jobId: label, label, available: 0, produced: 0 }));
  const clickable = !!(group && onClickGroup);

  return (
    <group position={position}>
      <mesh position={[0, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[r * 1.55, 0.04, 10, 32]} />
        <meshBasicMaterial color="#F59E0B" transparent opacity={0.9} />
      </mesh>
      <Html position={[0, lift, 0]} center>
        <div
          onClick={clickable ? () => onClickGroup(group) : undefined}
          title={
            clickable
              ? `One physical bobbin carrying ${rows.length} products — a merge run. Opens all ${rows.length} together.`
              : 'One physical bobbin carrying several products — a merge run.'
          }
          style={{
            background: 'rgba(20,14,4,0.94)',
            border: '1px solid #F59E0B',
            borderRadius: 5,
            padding: '3px 6px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            pointerEvents: 'auto',
            cursor: clickable ? 'pointer' : 'help',
            minWidth: 92
          }}
        >
          <div style={{
            color: '#F59E0B', fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
            textAlign: 'center', marginBottom: 2
          }}>
            ⇄ MERGE · {rows.length}
          </div>
          {rows.map(s => (
            <div key={s.jobId} style={{
              display: 'flex', justifyContent: 'space-between', gap: 8,
              fontSize: 8, color: '#FFF', lineHeight: 1.35
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 92 }}>
                {s.label}
              </span>
              {(s.available || s.produced) > 0 && (
                <span style={{ color: '#FBBF24', fontWeight: 700 }}>
                  {Math.round(s.available || s.produced)}m
                </span>
              )}
            </div>
          ))}
          <div style={{ fontSize: 6, color: '#A98A4B', textAlign: 'center', marginTop: 2, letterSpacing: 0.4 }}>
            {clickable ? `ONE DRUM · CLICK FOR ALL ${rows.length}` : 'ONE DRUM'}
          </div>
        </div>
      </Html>
    </group>
  );
}

function FocusRing({ position, size = 1.2 }) {
  const r = size / 2;
  return (
    <group position={position}>
      <mesh position={[0, 0.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[r * 1.35, 0.05, 10, 32]} />
        <meshBasicMaterial color="#22C55E" transparent opacity={0.85} />
      </mesh>
      <mesh position={[0, r + 0.5, 0]}>
        <cylinderGeometry args={[r * 0.9, r * 0.9, r + 1, 16]} />
        <meshBasicMaterial color="#22C55E" transparent opacity={0.12} />
      </mesh>
    </group>
  );
}

// Reports a located focus token/group world position up to the page (once mounted),
// so the camera can fly to it. `wide` asks for a pulled-back, group-framing view.
// Fires in an effect — never during render.
function FocusReporter({ position, group, wide, onLocated }) {
  useEffect(() => {
    if (typeof onLocated === 'function') onLocated({ pos: position, group, wide });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position[0], position[2], group?.jobId, group?.phase, wide]);
  return null;
}

// True when this token is the deep-linked focus token.
const isFocusTok = (tok, focusToken) =>
  focusToken &&
  String(tok.jobId) === String(focusToken.jobId) &&
  String(tok.tokenId) === String(focusToken.tokenId);

// True when the deep-linked token belongs to this whole product group.
const groupHasFocus = (group, focusToken) =>
  !!focusToken && (group?.tokens || []).some(t => isFocusTok(t, focusToken));

// No extra API call needed — nextPhaseName comes directly from /physical-tokens

// ─── 3D DRUM MODEL ─────────────────────────────────────────

function Drum3D({ position, color, phase, material, size = 1.2, consuming = false, active = false }) {
  const r = size / 2;
  const metal = isMetalDrum(material);
  const flangeColor = metal ? FLANGE_METAL : FLANGE_WOOD;
  const cableColor = cableColorFor(phase, color);
  const isCopper = phase === 'unwinding' || phase === 'pre_stranding';
  return (
    <group position={position}>
      {/* ON MACHINE glow + label */}
      {consuming && (
        <>
          <mesh position={[0, r + 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[r * 1.05, 0.04, 8, 24]} />
            <meshBasicMaterial color="#22C55E" transparent opacity={0.8} />
          </mesh>
          <Html position={[0, size + 0.5, 0]} center>
            <div style={{
              background: '#22C55E', color: '#000', padding: '1px 6px',
              borderRadius: 3, fontSize: 7, fontWeight: 800,
              fontFamily: 'monospace', whiteSpace: 'nowrap',
              userSelect: 'none', pointerEvents: 'none',
              animation: 'pulse 1.5s infinite'
            }}>
              ON MACHINE
            </div>
            <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}`}</style>
          </Html>
        </>
      )}
      {/* ACTIVE (being produced) glow + label */}
      {active && !consuming && (
        <>
          <mesh position={[0, r + 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[r * 1.05, 0.04, 8, 24]} />
            <meshBasicMaterial color="#3B82F6" transparent opacity={0.8} />
          </mesh>
          <Html position={[0, size + 0.5, 0]} center>
            <div style={{
              background: '#3B82F6', color: '#FFF', padding: '1px 6px',
              borderRadius: 3, fontSize: 7, fontWeight: 800,
              fontFamily: 'monospace', whiteSpace: 'nowrap',
              userSelect: 'none', pointerEvents: 'none',
              animation: 'pulse 1.5s infinite'
            }}>
              ACTIVE
            </div>
            <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}`}</style>
          </Html>
        </>
      )}
      {/* Drum barrel — sits on floor (Y=0.22 is floor level) */}
      <mesh position={[0, r + 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[r * 0.4, r * 0.4, r * 1.1, 12]} />
        <meshStandardMaterial color={consuming ? '#3A6030' : active ? '#2A3A5A' : '#5C4020'} roughness={0.8} />
      </mesh>
      {/* Flanges — blue + metallic for metal drums, wood-tone otherwise */}
      <mesh position={[0, r + 0.24, r * 0.58]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[r, r, 0.025, 14]} />
        <meshStandardMaterial color={flangeColor} roughness={metal ? 0.35 : 0.7} metalness={metal ? 0.6 : 0.05} />
      </mesh>
      <mesh position={[0, r + 0.24, -r * 0.58]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[r, r, 0.025, 14]} />
        <meshStandardMaterial color={flangeColor} roughness={metal ? 0.35 : 0.7} metalness={metal ? 0.6 : 0.05} />
      </mesh>
      {/* Cable wound — uniform radius so it sits level on both flanges (no cone) */}
      {cableColor && (
        <mesh position={[0, r + 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[r * 0.82, r * 0.82, r * 1.02, 16]} />
          <meshStandardMaterial color={cableColor} roughness={isCopper ? 0.5 : 0.9} metalness={isCopper ? 0.35 : 0.05} />
        </mesh>
      )}
    </group>
  );
}

// ─── BOBIN INFO LABEL ──────────────────────────────────────

// Per-drum label removed for performance — details via group click

// Label for a GROUP of drums (by jobId + phase) — clickable
function GroupLabel({ position, group, onClickGroup }) {
  const phaseLabel = PHASE_LABEL[group.phase] || group.phase;
  const held = allTokensOf(group);
  const totalAvail = Math.round(held.reduce((s, t) => s + (t.availableLength || 0), 0));
  const totalProg = Math.round(held.reduce((s, t) => s + (t.programmedLength || 0), 0));
  const fullyProg = totalProg >= totalAvail && totalProg > 0;
  const partialProg = totalProg > 0 && totalProg < totalAvail;
  const consumingCount = held.filter(t => t.status === 'CONSUMING').length;
  const activeCount = held.filter(t => t.status === 'ACTIVE').length;
  const phantomCount = held.filter(isPhantomToken).length;
  const allPhantom = phantomCount > 0 && phantomCount === held.length;
  const sColor = phantomCount > 0 ? PHANTOM_HEX
    : consumingCount > 0 ? '#F59E0B' : activeCount > 0 ? '#3B82F6' : '#6B7280';

  // Order/stock destination (customer name, "STOCK", or a mix for shared drums)
  const dest = destinationInfo(group);
  const split = lotSplit(group);
  const reservedUnits = promisedUnits(group);
  let destLabel = '', destColor = '#9CA3AF', destBg = 'rgba(107,114,128,0.18)', destBorder = '#6B728055';
  if (dest) {
    if (dest.kind === 'STOCK') {
      destLabel = 'STOCK';
    } else if (dest.kind === 'MIXED') {
      destLabel = dest.clients.length ? `STOCK + ${dest.clients.join(', ')}` : 'STOCK + ORDER';
      destColor = '#FBBF24'; destBg = 'rgba(245,158,11,0.18)'; destBorder = '#F59E0B55';
    } else {
      destLabel = dest.clients.length ? dest.clients.join(', ') : 'ORDER';
      destColor = '#4ADE80'; destBg = 'rgba(34,197,94,0.18)'; destBorder = '#22C55E55';
    }
  }

  return (
    <Html position={position} center>
      <div onClick={() => onClickGroup && onClickGroup(group)} style={{
        background: 'rgba(10,15,25,0.95)', color: '#FFF', padding: '4px 8px',
        borderRadius: 4, fontSize: 8, fontFamily: 'monospace',
        border: `1px solid ${sColor}60`, cursor: 'pointer',
        whiteSpace: 'nowrap', minWidth: 80
      }}>
        <div style={{ fontWeight: 700, fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
          {group.product}
        </div>
        {/* Above the STOCK line on purpose: STOCK says nobody has ordered it,
            PHANTOM says nothing made it. A phantom is always STOCK, so the two
            badges stacked read as one story rather than a contradiction. */}
        {phantomCount > 0 && (
          <div
            title="Phantom — stock declared into the system, not produced on a machine. No run, no operator, no recorded metres."
            style={{
              marginTop: 2, fontSize: 7, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
              textAlign: 'center', background: 'rgba(139,92,246,0.18)', color: PHANTOM_HEX,
              border: `1px solid ${PHANTOM_HEX}55`, whiteSpace: 'nowrap'
            }}>
            ◇ PHANTOM{allPhantom ? '' : ` ${phantomCount}/${held.length}`}
          </div>
        )}
        {dest && (
          <div title={destLabel} style={{
            marginTop: 2, fontSize: 7, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
            textAlign: 'center', background: destBg, color: destColor,
            border: `1px solid ${destBorder}`,
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120, whiteSpace: 'nowrap'
          }}>
            {destLabel}
          </div>
        )}
        <div style={{ fontSize: 7, color: '#AAA', marginTop: 1 }}>
          PHASE: <span style={{ color: '#DDD' }}>{phaseLabel}</span>
          <span style={{ marginLeft: 4, opacity: 0.6 }}>{held.length}x</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
          <span style={{ color: '#888', fontSize: 7 }}>Total</span>
          <span style={{ fontWeight: 700 }}>{totalAvail}m</span>
        </div>
        {/* How the lot divides, and how much of it is pinned to actual drums.
            The destination line above says WHO it is for; this says how much is
            still free — the number somebody looking for reservable metres is
            after, and the only one that told them to walk over here. */}
        {split && split.total > 0 && (
          <div style={{ marginTop: 2 }}>
            <div style={{ display: 'flex', height: 3, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${split.pct}%`, background: '#38BDF8' }} />
              <div style={{ width: `${100 - split.pct}%`, background: '#4ADE80' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 7, marginTop: 1 }}>
              <span style={{ color: '#38BDF8' }}>
                {split.toOrders > 0 ? `${Math.round(split.toOrders)}m order` : ''}
              </span>
              <span style={{ color: '#4ADE80' }}>
                {split.toStock > 0 ? `${Math.round(split.toStock)}m free` : ''}
              </span>
            </div>
          </div>
        )}
        {reservedUnits > 0 && (
          <div style={{
            marginTop: 2, fontSize: 7, fontWeight: 700, padding: '1px 4px',
            borderRadius: 2, textAlign: 'center',
            background: 'rgba(56,189,248,0.18)', color: '#38BDF8',
            border: '1px solid #38BDF855'
          }}>
            {reservedUnits} unit{reservedUnits === 1 ? '' : 's'} promised
          </div>
        )}
        {/* Status line */}
        {(consumingCount > 0 || activeCount > 0) && (
          <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
            {consumingCount > 0 && (
              <div style={{
                fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 2,
                background: '#F59E0B30', color: '#FBBF24', border: '1px solid #F59E0B50'
              }}>
                CONSUMING ({consumingCount})
              </div>
            )}
            {activeCount > 0 && (
              <div style={{
                fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 2,
                background: '#3B82F630', color: '#60A5FA', border: '1px solid #3B82F650'
              }}>
                ACTIVE ({activeCount})
              </div>
            )}
          </div>
        )}
        {/* PROG tag */}
        <div style={{
          marginTop: 2, fontSize: 7, fontWeight: 700, padding: '1px 3px', borderRadius: 2, textAlign: 'center',
          background: fullyProg ? '#22C55E30' : partialProg ? '#F59E0B30' : '#EF444430',
          color: fullyProg ? '#4ADE80' : partialProg ? '#FBBF24' : '#F87171',
          border: `1px solid ${fullyProg ? '#22C55E50' : partialProg ? '#F59E0B50' : '#EF444450'}`
        }}>
          {fullyProg ? 'PROG' : partialProg ? `PARTIAL ${totalProg}m` : 'NOT PROG'}
        </div>
      </div>
    </Html>
  );
}

// ─── GROUP HEADER ──────────────────────────────────────────

function GroupHeader({ position, product, count }) {
  return (
    <Html position={position} center>
      <div style={{
        background: 'rgba(20,30,50,0.95)', color: '#FFF', padding: '4px 10px',
        borderRadius: 4, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap',
        border: '1px solid #4A5568', userSelect: 'none', pointerEvents: 'none'
      }}>
        {product} <span style={{ opacity: 0.5 }}>({count})</span>
      </div>
    </Html>
  );
}

// ─── PRODUCTION BOBINS LAYOUT IN HALL 1 ────────────────────
// Isolation/unwinding tokens: between DTB1250 and L120 Take-up 2000
// Other phase tokens: next to L75 and L120 lines

// ─── BOBIN GROUP DETAIL PANEL (overlay) ─────────────────────

export function BobinGroupPanel({ group, onClose, autoPlan = false }) {
  const [step, setStep] = useState('tokens'); // tokens | planning | wizard | error
  const [phaseCtx, setPhaseCtx] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [result, setResult] = useState(null);
  const [wizardRows, setWizardRows] = useState(null);

  // When opened via a Factory-3D deep link, jump straight to the planning context
  // (fetchContext is defined below; group is always present at mount here).
  useEffect(() => {
    if (autoPlan && group) fetchContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!group) return null;
  const phaseLabel = PHASE_LABEL[group.phase] || group.phase;
  const totalAvail = Math.round(
    allTokensOf(group).reduce((s, t) => s + (t.availableLength || 0), 0)
  );

  const fetchContext = async () => {
    setPlanning(true);
    try {
      // Deduplicate by jobId:tokenId
      const seen = new Set();
      const items = [];
      for (const t of allTokensOf(group)) {
        const key = `${t.jobId}:${t.tokenId}`;
        if (!seen.has(key) && t.jobId && t.tokenId) {
          seen.add(key);
          items.push({ jobId: t.jobId.trim(), tokenId: t.tokenId.trim() });
        }
      }
      const res = await http.post('/api/redis/jobs/next-phase-context', { items });
      setPhaseCtx(res.data);
      setStep('planning');
    } catch (e) {
      setResult({ error: e.response?.data?.message || e.message });
      setStep('error');
    }
    setPlanning(false);
  };

  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#1a1f2e', color: '#FFF', borderRadius: 12,
        width: step === 'wizard' ? '95%' : '90%',
        maxWidth: step === 'wizard' ? 1000 : 650,
        maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        border: '2px solid #4A5568', boxShadow: '0 0 40px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: 10, background: '#12151f' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {group.product}
              {(() => {
                const dest = destinationInfo(group);
                if (!dest) return null;
                const isStock = dest.kind === 'STOCK';
                const isMixed = dest.kind === 'MIXED';
                const label = isStock ? 'STOCK'
                  : isMixed ? (dest.clients.length ? `STOCK + ${dest.clients.join(', ')}` : 'STOCK + ORDER')
                  : (dest.clients.length ? dest.clients.join(', ') : 'ORDER');
                const c = isStock ? '#9CA3AF' : isMixed ? '#FBBF24' : '#4ADE80';
                return (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: `${c}22`, color: c, border: `1px solid ${c}55`
                  }}>
                    {label}
                  </span>
                );
              })()}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>
              Phase: {phaseLabel} | {allTokensOf(group).length} tokens | {totalAvail}m total
            </div>
            {/* Opened from the orange card on a shared drum: every product on
                that bobbin at once, so planning the next phase covers all of
                them in one pass instead of one product at a time. */}
            {group.merged && (
              <div style={{
                marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                background: 'rgba(245,158,11,0.12)', border: '1px solid #F59E0B55',
                borderRadius: 6, padding: '4px 8px'
              }}>
                <span style={{ color: '#FBBF24', fontWeight: 700, fontSize: 11 }}>
                  ⇄ MERGE — one drum, {group.mergeShares?.length || allTokensOf(group).length} products
                </span>
                {(group.mergeShares || []).map(sh => (
                  <span key={sh.jobId} style={{ fontSize: 10, color: '#DDD' }}>
                    {sh.label}
                    <b style={{ color: '#FBBF24', marginLeft: 4 }}>
                      {Math.round(sh.available || sh.produced)}m
                    </b>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 4 }}>
            {['tokens', 'planning', 'success'].map((s, i) => (
              <div key={s} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: step === s ? '#3B82F6' : i <= ['tokens', 'planning', 'success'].indexOf(step) ? '#22C55E' : '#333'
              }} />
            ))}
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', padding: 4, fontSize: 20 }}>✕</div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {step === 'tokens' && (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #333', color: '#888' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Token</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Color</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Available</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Produced</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Prog</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Drums drawn elsewhere are listed here too. The product
                      genuinely holds a share of them — leaving them out would
                      make the panel disagree with the metres above it, and
                      "where is my BLUE drum" would have no answer. */}
                  {[...group.tokens, ...(group.alsoHolds || [])].map(tok => {
                    const sColor = STATUS_COLORS[tok.status] || '#888';
                    const progLen = Math.round(tok.programmedLength || 0);
                    const shared = tok.sharedWith || [];
                    return (
                      <tr key={tok.tokenId} style={{ borderBottom: '1px solid #1A1F2A' }}>
                        <td style={{ padding: '4px 6px', fontSize: 10 }}>
                          {tok.tokenId?.slice(0, 12)}
                          {shared.length > 0 && (
                            <span
                              title={`One physical bobbin, shared with: ${shared.join(', ')}. It is drawn once on the floor.`}
                              style={{ color: '#F59E0B', marginLeft: 4, cursor: 'help' }}
                            >
                              ⇄
                            </span>
                          )}
                          {isPhantomToken(tok) && (
                            <span
                              title="Phantom — stock declared into the system, not produced on a machine. Its Produced figure is a declaration, not a measurement."
                              style={{ color: PHANTOM_HEX, marginLeft: 4, cursor: 'help' }}
                            >
                              ◇
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '4px 6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: COLOR_HEX[tok.color] || '#888' }} />
                            <span style={{ fontSize: 9 }}>{tok.color || '—'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{Math.round(tok.availableLength || 0)}m</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{Math.round(tok.producedLength || 0)}m</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <span style={{ color: sColor, fontWeight: 600, fontSize: 9 }}>{tok.status}</span>
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: progLen > 0 ? '#4ADE80' : '#F87171' }}>
                          {progLen > 0 ? `${progLen}m` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Order context and reservations, in the panel's own style.
                  The data is already on the tokens — the destination carries
                  each order's delivery plan and how many units this product has
                  promised of each — so this costs no extra call.

                  Deliberately no lot traceability here: the token table above
                  already IS the product's structure. What was missing was the
                  other side of it — who the cable is for and what has been
                  pinned to whom. */}
              {(() => {
                // allTokensOf: a group whose only drum is shared has it drawn
                // under the other product, so `group.tokens` can be empty here
                // and the order / job would vanish from the panel.
                const dest = allTokensOf(group).find(t => t && t.destination)
                  ?.destination;
                const orders = (dest?.orders || []).filter(o => o.orderCode);
                const jobId = allTokensOf(group).find(t => t?.jobId)?.jobId;
                if (!orders.length && !jobId) return null;

                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                      Orders &amp; reservations
                    </div>

                    {!orders.length && (
                      <div style={{ fontSize: 10, color: '#888' }}>
                        Nothing promised — this product is all stock.
                      </div>
                    )}

                    {orders.map((o, oi) => (
                      <div
                        key={oi}
                        style={{
                          background: '#0D1117',
                          border: '1px solid #2A2F3A',
                          borderRadius: 6,
                          padding: 8,
                          marginBottom: 6
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ fontWeight: 700 }}>
                            {o.orderCode}
                            {o.client && (
                              <span style={{ color: '#888', fontWeight: 400 }}>
                                {' '}· {o.client}
                              </span>
                            )}
                          </span>
                          <span style={{ fontWeight: 700, color: '#38BDF8' }}>
                            {Math.round(Number(o.length) || 0).toLocaleString()}m
                          </span>
                        </div>
                        {o.dueDate && (
                          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
                            due {o.dueDate}
                          </div>
                        )}
                        {(o.deliveryItems || []).map((it, ii) => (
                          <div
                            key={ii}
                            style={{ fontSize: 9, color: '#BBB', marginTop: 3 }}
                          >
                            {it.qtyPlanned} ×{' '}
                            {String(it.kind).toUpperCase() === 'COIL'
                              ? 'coil'
                              : [
                                  it.drumMaterial,
                                  it.drumDiameter ? `Ø${it.drumDiameter}` : ''
                                ]
                                  .filter(Boolean)
                                  .join(' ') || 'drum'}{' '}
                            {Number(it.lengthPerUnit).toLocaleString()}m
                            {it.reserved > 0 && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  padding: '0 4px',
                                  borderRadius: 2,
                                  background: 'rgba(56,189,248,0.18)',
                                  color: '#38BDF8',
                                  fontWeight: 700
                                }}
                              >
                                {it.reserved} promised
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* The allocation mechanism itself — same modal as
                        everywhere else, so the decision is made one way. */}
                    {jobId && (
                      <div className="mt-2">
                        <ProductAllocationButton jobId={jobId} />
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {step === 'planning' && phaseCtx && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Next Phase Context</div>
              {(phaseCtx.rows || []).map((row, ri) => (
                <div key={ri} style={{
                  background: '#0D1117', borderRadius: 6, padding: 10, marginBottom: 8,
                  border: '1px solid #2A2F3A'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span>
                      <span style={{ color: COLOR_HEX[row.token?.color] || '#888' }}>●</span>
                      {' '}{row.token?.tokenId?.slice(0, 12)}
                    </span>
                    <span style={{ fontWeight: 700 }}>{Math.round(row.token?.availableLength || 0)}m</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#AAA', marginTop: 4 }}>
                    {row.fromPhase} → <span style={{ color: row.nextPhase ? '#4ADE80' : '#F87171', fontWeight: 700 }}>
                      {row.nextPhase?.name || 'No next phase'}
                    </span>
                  </div>
                  {row.orders?.length > 0 && (
                    <div style={{ fontSize: 9, color: '#888', marginTop: 3 }}>
                      Orders: {row.orders.map(o => o.orderCode || o.orderId?.slice(0, 8)).join(', ')}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700 }}>
                Total: {totalAvail}m | {phaseCtx.count} tokens ready
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: '#888', background: '#0D1117', padding: 8, borderRadius: 4 }}>
                To complete planning (select machine, drum type, lengths), click "Open Full Wizard" below.
                The wizard will open and Factory 3D stays here.
              </div>
            </div>
          )}

          {step === 'wizard' && wizardRows && (
            <div style={{ minHeight: 400 }}>
              <Suspense fallback={<div style={{ textAlign: 'center', padding: 30, opacity: 0.5 }}>Loading wizard...</div>}>
                <NextPhaseWizzard />
              </Suspense>
            </div>
          )}

          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: 30 }}>
              <div style={{ fontSize: 40, color: '#EF4444' }}>✕</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#EF4444', marginTop: 8 }}>
                Error
              </div>
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                {result?.error || 'Something went wrong'}
              </div>
            </div>
          )}
        </div>

        {/* Actions — hidden when wizard is active (wizard has its own buttons) */}
        {step !== 'wizard' && <div style={{ padding: '10px 16px', borderTop: '1px solid #333', display: 'flex', gap: 8, justifyContent: 'flex-end', background: '#12151f' }}>
          {step === 'tokens' && (
            <>
              <button onClick={onClose} style={{
                padding: '6px 16px', borderRadius: 4, border: '1px solid #555',
                background: 'transparent', color: '#AAA', cursor: 'pointer', fontSize: 12
              }}>Close</button>
              <button onClick={fetchContext} disabled={planning} style={{
                padding: '6px 16px', borderRadius: 4, border: 'none',
                background: '#3B82F6', color: '#FFF', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                opacity: planning ? 0.6 : 1
              }}>
                {planning ? 'Loading...' : 'Plan Next Phase →'}
              </button>
            </>
          )}
          {step === 'planning' && (
            <>
              <button onClick={() => setStep('tokens')} style={{
                padding: '6px 16px', borderRadius: 4, border: '1px solid #555',
                background: 'transparent', color: '#AAA', cursor: 'pointer', fontSize: 12
              }}>← Back</button>
              <button onClick={() => {
                const rows = allTokensOf(group).map(t => ({ ...t, original: t }));
                const tokenIds = [...new Set(rows.map(r => r.tokenId))];
                // Store in sessionStorage — the wizard reads it as fallback
                sessionStorage.setItem('factory3d_wizard_state', JSON.stringify({
                  from: 'Factory3D', tokenIds, rows
                }));
                setWizardRows(rows);
                setStep('wizard');
              }} style={{
                padding: '6px 16px', borderRadius: 4, border: 'none',
                background: '#22C55E', color: '#FFF', cursor: 'pointer', fontSize: 12, fontWeight: 700
              }}>
                Open Wizard
              </button>
            </>
          )}
          {step === 'error' && (
            <button onClick={onClose} style={{
              padding: '6px 16px', borderRadius: 4, border: 'none',
              background: '#3B82F6', color: '#FFF', cursor: 'pointer', fontSize: 12, fontWeight: 700
            }}>Close</button>
          )}
        </div>}
      </div>
    </div>
  );
}

// ─── PHASE FILTER BAR ───────────────────────────────────────

const ALL_PHASES = [
  { key: 'all', label: 'All' },
  { key: 'conductor_isolation', label: 'Isolation' },
  { key: 'unwinding', label: 'Unwinding' },
  { key: 'rope', label: 'Rope' },
  { key: 'interior_sheat', label: 'Inner' },
  { key: 'exterior_sheat', label: 'Outer' },
  { key: 'pre_stranding', label: 'Pre-Strand' },
  { key: 'rewinding', label: 'Rewind' },
  { key: 'coiling', label: 'Coil' }
];

export function PhaseFilterBar({ activePhase, onChange }) {
  return (
    <div style={{
      position: 'absolute', top: 50, left: '50%', transform: 'translateX(-50%)',
      display: 'flex', gap: 3, background: 'rgba(20,20,35,0.9)',
      padding: '4px 8px', borderRadius: 20, backdropFilter: 'blur(10px)',
      zIndex: 50
    }}>
      {ALL_PHASES.map(p => (
        <button key={p.key} onClick={() => onChange(p.key)} style={{
          padding: '3px 10px', borderRadius: 12, border: 'none', fontSize: 10, fontWeight: 600,
          cursor: 'pointer',
          background: activePhase === p.key ? '#3B82F6' : 'transparent',
          color: activePhase === p.key ? '#FFF' : '#888'
        }}>
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ─── PRODUCTION BOBINS LAYOUT IN HALL 1 ────────────────────

export function Hall1ProductionBobins({ onSelectGroup, focusToken, onFocusLocated }) {
  const { data } = useBobinsState();

  const { isolationGroups, otherGroups } = useMemo(() => {
    if (!data?.tokens) return { isolationGroups: [], otherGroups: [] };

    // Show all real tokens — CONSUMING (on machine input) and ACTIVE (being produced) included.
    // PHANTOMS (stock, e.g. completed COPPER_ROPE held as pre_stranding stock) ARE
    // included so they show in the hall as available stock — they were previously
    // hidden, so a finished COPPER_ROPE never appeared.
    // Exclude tokens already IN coiling/rewinding phase — they go to packing station.
    // Also exclude FINAL drums (deliveryMode final_product / coils_on_machine):
    // they're done and live in the YARD (YardVisualization), not the hall flow.
    const FINAL_DELIVERY = ['final_product', 'coils_on_machine'];
    const tokens = data.tokens.filter(t =>
      !HALL3_PHASES.includes(t.phase) &&
      !FINAL_DELIVERY.includes(String(t.deliveryMode || '').toLowerCase())
    );

    // Routing based on nextPhaseName (from backend):
    // - nextPhaseName is coiling/rewinding → Hall 3
    // - nextPhaseName is armouring → Hall 4
    // - everything else → Hall 1
    const hall1Tokens = tokens.filter(t => {
      const np = t.nextPhaseName;
      if (np && HALL3_PHASES.includes(np)) return false;
      if (np === ARMOURING_PHASE) return false;
      return true;
    });

    // Split by phase type — isolation/unwinding vs other production phases
    const isolation = hall1Tokens.filter(t => ISOLATION_PHASES.includes(t.phase));
    const others = hall1Tokens.filter(t => !ISOLATION_PHASES.includes(t.phase));

    // One bobbin under several products — a merge-execution or a partial fork
    // leaves the SAME tokenId under two jobs, and /physical-tokens emits a row
    // per (job, token). Drawn naively that is TWO drums on the floor for one
    // physical object, in two different product groups, and the hall then shows
    // more drums than the factory has.
    const sharedIndex = buildSharedTokenIndex(tokens);
    // …and HOW it divides, so the drum can state the merge itself instead of
    // each product floating its own card at the same spot.
    const shareIndex = sharedShareIndex(tokens);

    // What the orange card on a shared drum opens: every product on that
    // bobbin as one group, which the panel plans from in a single pass.
    const mergeGroups = buildMergeGroups(tokens);

    // Group by product, drawing each physical drum ONCE.
    const groupBy = (arr) => {
      const map = {};
      // A tokenId already placed by an earlier group is the same object; the
      // later group does not get a second copy of it, it gets told about it.
      const placed = new Set();

      for (const t of arr) {
        const key = `${t.jobId}::${t.phase}`;
        if (!map[key]) map[key] = { product: t.productLabel, phase: t.phase, jobId: t.jobId, tokens: [] };

        const tid = String(t.tokenId || '');
        if (tid && placed.has(tid)) {
          // Not drawn again, but the group still knows it owns a share of it —
          // the panel lists it so "where is my BLUE drum" has an answer.
          map[key].alsoHolds = map[key].alsoHolds || [];
          map[key].alsoHolds.push({
            ...t,
            sharedWith: sharedPartners(sharedIndex, t.tokenId, t.jobId)
          });
          continue;
        }
        if (tid) placed.add(tid);

        map[key].tokens.push({
          ...t,
          // the OTHER products holding this same bobbin
          sharedWith: sharedPartners(sharedIndex, t.tokenId, t.jobId),
          // every holder and its metres — this is the drum that gets drawn, so
          // this is where the merge is stated
          mergeShares: shareIndex.get(String(t.tokenId || '')) || null,
          // the whole merge as one openable group — see mergeGroups above
          mergeGroup: mergeGroups.get(String(t.tokenId || '')) || null
        });
      }
      return Object.values(map);
    };

    // Sort other groups by phase order so they're visually organized
    const PHASE_ORDER = ['rope', 'interior_sheat', 'exterior_sheat', 'pre_stranding', 'rewinding'];
    const sorted = groupBy(others).sort((a, b) => {
      const ai = PHASE_ORDER.indexOf(a.phase);
      const bi = PHASE_ORDER.indexOf(b.phase);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return { isolationGroups: groupBy(isolation), otherGroups: sorted };
  }, [data]);

  // Hall 1: X=-28 to -4
  // Machines: RX=-6.5 (L75 line), LX=-25.5 (L120/DTB line)
  //
  // ISOLATION/UNWINDING: between Take-up 2000 (Z=43) and DTB (Z=60), near left wall
  // OTHER PHASES:
  //   L120 side: Z limits = Caterpillar(Z=17) to Dancer(Z=39), X starts at LX+2 going toward center
  //   L75 side:  Z limits = Dancer(Z=19) to Extruder(Z=37), X starts at RX-2 going toward center
  //   Multiple rows in front of each other

  const DZ = 1.5;  // row spacing along Z (bigger drums)
  const DX = 1.5;  // depth spacing (front-to-back rows)

  // Isolation zone — fixed 8 rows x 9 columns matrix
  // Rows along Z (away from Take-up), columns along X (wall → center)
  // Phase 1: 8 rows x 9 cols = 72 drums
  // Phase 2: overflow continues from row 4 toward octabins (Z extending)
  const ISO_ROWS = 8;     // along Z
  const ISO_COLS = 9;     // along X (wall → center)
  const isoXwall = -26;   // first column X (2m from wall at -28)
  const isoZstart = 47.5;  // 1 drum closer to Take-up 2000
  const isoCapPhase1 = ISO_ROWS * ISO_COLS; // 72
  // Phase 2 overflow: 6 cols starting from column 4 of the matrix
  // Rows extend toward octabins (Z direction), stop before octabin position (Z=81)
  const ISO_OVF_COLS = 6;
  const isoOverflowXstart = isoXwall + 3 * DX; // start at column 4 (0-indexed col 3)
  const isoOverflowZstart = isoZstart + ISO_ROWS * DZ + 0.5; // right after the matrix
  const isoOverflowZend = 80; // stop before octabins at Z=81

  // L120 side limits — in front of extruders, toward center
  const l120Zmin = 17;
  const l120Zmax = 39;
  const l120Xstart = -21; // more toward center (machines at -25.5)
  const l120SlotsPerRow = Math.floor((l120Zmax - l120Zmin) / DZ);

  // L75 side limits — in front of machines, toward center
  const l75Zmin = 19;
  const l75Zmax = 37;
  const l75Xstart = -11; // more toward center (machines at -6.5)
  const l75SlotsPerRow = Math.floor((l75Zmax - l75Zmin) / DZ);

  return (
    <group>
      {/* ═══ ISOLATION/UNWINDING — grouped by job ═══════════ */}
      {(() => {
        // Helper: get position for a global drum index
        const getPos = (idx) => {
          if (idx < isoCapPhase1) {
            // Phase 1: 8 rows x 9 cols matrix
            const row = idx % ISO_ROWS;  // along Z
            const col = Math.floor(idx / ISO_ROWS); // along X (wall → center)
            return [isoXwall + col * DX, isoZstart + row * DZ];
          } else {
            // Phase 2: 6 cols from col 4, rows extend toward octabins
            const idx2 = idx - isoCapPhase1;
            const col = idx2 % ISO_OVF_COLS;
            const row = Math.floor(idx2 / ISO_OVF_COLS);
            const z = isoOverflowZstart + row * DZ;
            return [
              isoOverflowXstart + col * DX,
              Math.min(z, isoOverflowZend) // don't go past octabins
            ];
          }
        };

        let slotIdx = 0;
        return isolationGroups.map((group, gi) => {
          // A product whose EVERY drawn drum is shared has no card of its own to
          // show: the orange merge card on the drum is its label, and naming the
          // product twice at one spot is what put a card over a card. When the
          // group does keep its label, the merge card lifts above it so the two
          // stack instead of colliding.
          const allDrawnShared = group.tokens.length > 0 &&
            group.tokens.every(t => t.sharedWith?.length > 0);
          const showGroupLabel = group.tokens.length > 0 && !allDrawnShared;
          const mergeLift = showGroupLabel ? 3.5 : 2.2;
          const startSlot = slotIdx;
          const gFocus = groupHasFocus(group, focusToken);
          // isolation/unwinding: light up the WHOLE product group so it pops in the
          // dense matrix, and frame the whole group with the camera.
          const groupWide = gFocus && ISOLATION_PHASES.includes(group.phase);
          const [gcx0, gcz0] = getPos(startSlot);
          const [gcx1, gcz1] = getPos(startSlot + group.tokens.length - 1);
          const groupCenter = [(gcx0 + gcx1) / 2, 0, (gcz0 + gcz1) / 2];
          const elems = group.tokens.map((tok, ti) => {
            const [x, z] = getPos(startSlot + ti);
            const isTok = isFocusTok(tok, focusToken);
            const ringHere = groupWide && !isTok;
            return (
              <group key={tok.tokenId}>
                <Drum3D position={[x, 0, z]} color={tok.color} phase={tok.phase} material={tok.drum_material} consuming={tok.status === 'CONSUMING'} active={tok.status === 'ACTIVE'} />
                {isPhantomToken(tok) && <PhantomMarker position={[x, 0, z]} />}
                {tok.sharedWith?.length > 0 && (
                  <MergeCard position={[x, 0, z]} partners={tok.sharedWith} shares={tok.mergeShares}
                    group={tok.mergeGroup} onClickGroup={onSelectGroup} lift={mergeLift} />
                )}
                {ringHere && <FocusRing position={[x, 0, z]} />}
                {isTok && <FocusBeacon position={[x, 0, z]} label={groupWide ? '★ PRODUCT GROUP' : '★ THIS TOKEN'} />}
                {isTok && <FocusReporter position={groupWide ? groupCenter : [x, 0, z]} group={group} wide={groupWide} onLocated={onFocusLocated} />}
              </group>
            );
          });
          const [labelX, labelZ] = getPos(startSlot);
          slotIdx += group.tokens.length;
          return (
            <group key={`iso-g-${gi}`}>
              {showGroupLabel && (
                <GroupLabel position={[labelX, 2.2, labelZ]} group={group} onClickGroup={onSelectGroup} />
              )}
              {elems}
            </group>
          );
        });
      })()}

      {/* ═══ OTHER PHASES — L120 SIDE, grouped by job ═══════ */}
      {(() => {
        const half = Math.ceil(otherGroups.length / 2);
        const groups = otherGroups.slice(0, half);
        let slotIdx = 0;
        return groups.map((group, gi) => {
          // A product whose EVERY drawn drum is shared has no card of its own to
          // show: the orange merge card on the drum is its label, and naming the
          // product twice at one spot is what put a card over a card. When the
          // group does keep its label, the merge card lifts above it so the two
          // stack instead of colliding.
          const allDrawnShared = group.tokens.length > 0 &&
            group.tokens.every(t => t.sharedWith?.length > 0);
          const showGroupLabel = group.tokens.length > 0 && !allDrawnShared;
          const mergeLift = showGroupLabel ? 3.5 : 2.2;
          const startSlot = slotIdx;
          const elems = group.tokens.map((tok, ti) => {
            const idx = startSlot + ti;
            const row = idx % l120SlotsPerRow;
            const depth = Math.floor(idx / l120SlotsPerRow);
            const x = l120Xstart + depth * DX;
            const z = l120Zmin + row * DZ;
            const focus = isFocusTok(tok, focusToken);
            return (
              <group key={tok.tokenId}>
                <Drum3D position={[x, 0, z]} color={tok.color} phase={tok.phase} material={tok.drum_material} consuming={tok.status === 'CONSUMING'} active={tok.status === 'ACTIVE'} />
                {isPhantomToken(tok) && <PhantomMarker position={[x, 0, z]} />}
                {tok.sharedWith?.length > 0 && (
                  <MergeCard position={[x, 0, z]} partners={tok.sharedWith} shares={tok.mergeShares}
                    group={tok.mergeGroup} onClickGroup={onSelectGroup} lift={mergeLift} />
                )}
                {focus && <FocusBeacon position={[x, 0, z]} />}
                {focus && <FocusReporter position={[x, 0, z]} group={group} onLocated={onFocusLocated} />}
              </group>
            );
          });
          const firstRow = startSlot % l120SlotsPerRow;
          const firstDepth = Math.floor(startSlot / l120SlotsPerRow);
          const labelX = l120Xstart + firstDepth * DX;
          const labelZ = l120Zmin + firstRow * DZ;
          slotIdx += group.tokens.length;
          return (
            <group key={`l120-g-${gi}`}>
              {showGroupLabel && (
                <GroupLabel position={[labelX, 2.2, labelZ]} group={group} onClickGroup={onSelectGroup} />
              )}
              {elems}
            </group>
          );
        });
      })()}

      {/* ═══ OTHER PHASES — L75 SIDE, grouped by job ════════ */}
      {(() => {
        const half = Math.ceil(otherGroups.length / 2);
        const groups = otherGroups.slice(half);
        let slotIdx = 0;
        return groups.map((group, gi) => {
          // A product whose EVERY drawn drum is shared has no card of its own to
          // show: the orange merge card on the drum is its label, and naming the
          // product twice at one spot is what put a card over a card. When the
          // group does keep its label, the merge card lifts above it so the two
          // stack instead of colliding.
          const allDrawnShared = group.tokens.length > 0 &&
            group.tokens.every(t => t.sharedWith?.length > 0);
          const showGroupLabel = group.tokens.length > 0 && !allDrawnShared;
          const mergeLift = showGroupLabel ? 3.5 : 2.2;
          const startSlot = slotIdx;
          const elems = group.tokens.map((tok, ti) => {
            const idx = startSlot + ti;
            const row = idx % l75SlotsPerRow;
            const depth = Math.floor(idx / l75SlotsPerRow);
            const x = l75Xstart - depth * DX;
            const z = l75Zmin + row * DZ;
            const focus = isFocusTok(tok, focusToken);
            return (
              <group key={tok.tokenId}>
                <Drum3D position={[x, 0, z]} color={tok.color} phase={tok.phase} material={tok.drum_material} consuming={tok.status === 'CONSUMING'} active={tok.status === 'ACTIVE'} />
                {isPhantomToken(tok) && <PhantomMarker position={[x, 0, z]} />}
                {tok.sharedWith?.length > 0 && (
                  <MergeCard position={[x, 0, z]} partners={tok.sharedWith} shares={tok.mergeShares}
                    group={tok.mergeGroup} onClickGroup={onSelectGroup} lift={mergeLift} />
                )}
                {focus && <FocusBeacon position={[x, 0, z]} />}
                {focus && <FocusReporter position={[x, 0, z]} group={group} onLocated={onFocusLocated} />}
              </group>
            );
          });
          const firstRow = startSlot % l75SlotsPerRow;
          const firstDepth = Math.floor(startSlot / l75SlotsPerRow);
          const labelX = l75Xstart - firstDepth * DX;
          const labelZ = l75Zmin + firstRow * DZ;
          slotIdx += group.tokens.length;
          return (
            <group key={`l75-g-${gi}`}>
              {showGroupLabel && (
                <GroupLabel position={[labelX, 2.2, labelZ]} group={group} onClickGroup={onSelectGroup} />
              )}
              {elems}
            </group>
          );
        });
      })()}
    </group>
  );
}

// ─── HALL 3 PRODUCTION BOBINS (coiling phase) ──────────────
// Hall 3: X=11 to 35, Z=0 to 41
// ACH300 at WX=33, Z=6 | DHC600 at WX=33, Z=33
// Drums placed in the open area near the door wall (X≈14)

export function Hall3ProductionBobins({ onSelectGroup, focusToken, onFocusLocated }) {
  const { data } = useBobinsState();

  const groups = useMemo(() => {
    if (!data?.tokens) return [];
    // Tokens whose nextPhaseName is coiling/rewinding
    // Exclude tokens already IN coiling phase (they show as unpacked europallets)
    const tokens = data.tokens.filter(t => {
      if (t.isPhantom) return false;
      if (HALL3_PHASES.includes(t.phase)) return false;
      return t.nextPhaseName && HALL3_PHASES.includes(t.nextPhaseName);
    });
    // Group by product+phase, separate isolation tokens from cable-on-drum
    const map = {};
    for (const t of tokens) {
      // Use current phase for grouping — isolation tokens vs cable tokens
      const isIsolated = t.phase === 'conductor_isolation' || t.phase === 'unwinding';
      const groupPhase = isIsolated ? 'isolated_coils' : 'cable_on_drum';
      const key = `${t.jobId}::${groupPhase}`;
      if (!map[key]) map[key] = {
        product: t.productLabel,
        phase: groupPhase,
        jobId: t.jobId,
        tokens: []
      };
      map[key].tokens.push(t);
    }
    // Sort: isolated first, then cable
    const sorted = Object.values(map).sort((a, b) => {
      if (a.phase === 'isolated_coils' && b.phase !== 'isolated_coils') return -1;
      if (a.phase !== 'isolated_coils' && b.phase === 'isolated_coils') return 1;
      return 0;
    });
    return sorted;
  }, [data]);

  const DZ = 1.5;
  const DX = 1.5;
  // Both on LEFT wall (X=11 side), split by door (Z=17) with gap
  // Isolated coils: from small oven (Z≈3) to before door (Z≈14)
  const isoX = 13;
  const isoZ = 6;      // after small oven area
  const isoZend = 14;  // stop before door
  const isoSlotsPerCol = Math.floor((isoZend - isoZ) / DZ);
  // Cable on drum: after door (Z≈21) to big oven (Z≈38)
  const cableX = 13;
  const cableZ = 21;   // gap after door
  const cableZend = 38;
  const cableSlotsPerCol = Math.floor((cableZend - cableZ) / DZ);

  return (
    <group>
      {(() => {
        let isoIdx = 0;
        let cableIdx = 0;
        return groups.map((group, gi) => {
          // A product whose EVERY drawn drum is shared has no card of its own to
          // show: the orange merge card on the drum is its label, and naming the
          // product twice at one spot is what put a card over a card. When the
          // group does keep its label, the merge card lifts above it so the two
          // stack instead of colliding.
          const allDrawnShared = group.tokens.length > 0 &&
            group.tokens.every(t => t.sharedWith?.length > 0);
          const showGroupLabel = group.tokens.length > 0 && !allDrawnShared;
          const mergeLift = showGroupLabel ? 3.5 : 2.2;
          const isIso = group.phase === 'isolated_coils';
          const startSlot = isIso ? isoIdx : cableIdx;
          const perCol = isIso ? isoSlotsPerCol : cableSlotsPerCol;

          const elems = group.tokens.map((tok, ti) => {
            const idx = startSlot + ti;
            const row = idx % perCol;
            const col = Math.floor(idx / perCol);
            // Both start from left wall (X=13), rows grow toward center (+X)
            const x = (isIso ? isoX : cableX) + col * DX;
            const z = (isIso ? isoZ : cableZ) + row * DZ;
            const focus = isFocusTok(tok, focusToken);
            return (
              <group key={tok.tokenId}>
                <Drum3D position={[x, 0, z]} color={tok.color} phase={tok.phase} material={tok.drum_material} consuming={tok.status === 'CONSUMING'} active={tok.status === 'ACTIVE'} />
                {isPhantomToken(tok) && <PhantomMarker position={[x, 0, z]} />}
                {tok.sharedWith?.length > 0 && (
                  <MergeCard position={[x, 0, z]} partners={tok.sharedWith} shares={tok.mergeShares}
                    group={tok.mergeGroup} onClickGroup={onSelectGroup} lift={mergeLift} />
                )}
                {focus && <FocusBeacon position={[x, 0, z]} />}
                {focus && <FocusReporter position={[x, 0, z]} group={group} onLocated={onFocusLocated} />}
              </group>
            );
          });
          const firstRow = startSlot % perCol;
          const firstCol = Math.floor(startSlot / perCol);
          const labelX = (isIso ? isoX : cableX) + firstCol * DX;
          const labelZ = (isIso ? isoZ : cableZ) + firstRow * DZ;
          if (isIso) isoIdx += group.tokens.length; else cableIdx += group.tokens.length;
          return (
            <group key={`h3-g-${gi}`}>
              {showGroupLabel && (
                <GroupLabel
                  position={[labelX, 2.2, labelZ]}
                  group={group}
                  onClickGroup={onSelectGroup}
                />
              )}
              {elems}
            </group>
          );
        });
      })()}
      {/* ═══ UNPACKED COILS — produced but not yet on pallets ═══ */}
      <UnpackedCoilsDisplay />
    </group>
  );
}

// 3D europallet — same style as truck load simulator
function PalletWithCoils({ position, color = '#3a95c9' }) {
  const cargoH = 0.7;
  return (
    <group position={position}>
      {/* Pallet legs */}
      {[[-0.4, -0.25], [-0.4, 0.25], [0, 0], [0.4, -0.25], [0.4, 0.25]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.04, z]} castShadow>
          <boxGeometry args={[0.12, 0.08, 0.1]} />
          <meshStandardMaterial color="#6B4226" roughness={0.95} />
        </mesh>
      ))}
      {/* Pallet top boards */}
      {[-0.35, -0.12, 0.12, 0.35].map((x, i) => (
        <mesh key={`b-${i}`} position={[x, 0.09, 0]} castShadow>
          <boxGeometry args={[0.18, 0.02, 0.7]} />
          <meshStandardMaterial color="#8b5a2b" roughness={0.9} />
        </mesh>
      ))}
      {/* Cargo box */}
      <mesh position={[0, 0.1 + cargoH / 2, 0]} castShadow>
        <boxGeometry args={[0.9, cargoH, 0.6]} />
        <meshStandardMaterial color={color} roughness={0.6} />
      </mesh>
      {/* Shrink wrap */}
      <mesh position={[0, 0.1 + cargoH / 2, 0]}>
        <boxGeometry args={[0.92, cargoH + 0.02, 0.62]} />
        <meshStandardMaterial color="#E8E8F0" transparent opacity={0.12} roughness={0.2} />
      </mesh>
    </group>
  );
}

// Shows unpacked coils near ACH300 and DHC600
function UnpackedCoilsDisplay() {
  const { data: coils } = useQuery({
    queryKey: ['packing', 'coils-3d'],
    queryFn: async () => {
      const r = await httpBackend.get('/api/v1/packing/coils');
      return r.data?.data || [];
    },
    refetchInterval: 15000, staleTime: 10000
  });

  const { achCoils, dhcCoils } = useMemo(() => {
    if (!coils?.length) return { achCoils: [], dhcCoils: [] };
    const groupByProduct = (arr) => {
      const map = {};
      for (const c of arr) {
        const key = `${c.jobId}::${c.productLabel}`;
        if (!map[key]) map[key] = {
          product: c.productLabel, color: c.color,
          totalLength: 0, totalCoils: 0, coilLength: 0, count: 0
        };
        map[key].totalLength += c.remainingLength || 0;
        map[key].totalCoils += c.remainingCoils || c.coilCount || 0;
        if (c.coilLength) map[key].coilLength = c.coilLength;
        map[key].count++;
      }
      return Object.values(map);
    };
    // Split by producing machine
    const ach = coils.filter(c => (c.producedByMachine || '').toLowerCase() === 'ach300');
    const dhc = coils.filter(c => (c.producedByMachine || '').toLowerCase() === 'dhc600');
    // Unassigned (no machineId) go to ACH area
    const unassigned = coils.filter(c => !c.producedByMachine || !['ach300', 'dhc600'].includes((c.producedByMachine || '').toLowerCase()));
    return {
      achCoils: groupByProduct([...ach, ...unassigned]),
      dhcCoils: groupByProduct(dhc)
    };
  }, [coils]);

  // ACH300 area: in front of ACH300 machines (X≈29, Z=6→14)
  // DHC600 area: in front of DHC600 dual head (X≈29, Z=28→36)
  const achX = 29;
  const dhcX = 29;

  return (
    <group>
      {/* ACH300 unpacked coils */}
      {achCoils.map((group, gi) => (
        <group key={`ach-uc-${gi}`}>
          <PalletWithCoils
            position={[achX, 0, 8 + gi * 1.8]}
            color={COLOR_HEX[group.color] || '#555'}
            coilCount={group.totalCoils}
          />
          <Html position={[achX, 1.2, 8 + gi * 1.8]} center>
            <div style={{
              background: 'rgba(10,15,25,0.92)', color: '#FFF', padding: '4px 8px',
              borderRadius: 4, fontSize: 10, fontFamily: 'monospace',
              border: '1px solid #F59E0B60', userSelect: 'none', pointerEvents: 'none',
              whiteSpace: 'nowrap'
            }}>
              <div style={{ fontWeight: 700 }}>{group.product}</div>
              <div>{Math.round(group.totalLength)}m • {group.totalCoils} coils</div>
              {group.coilLength > 0 && <div style={{ color: '#AAA' }}>@ {group.coilLength}m/coil</div>}
              <div style={{ color: '#F59E0B', fontWeight: 700, marginTop: 2 }}>UNPACKED</div>
            </div>
          </Html>
        </group>
      ))}

      {/* DHC600 unpacked coils */}
      {dhcCoils.map((group, gi) => (
        <group key={`dhc-uc-${gi}`}>
          <PalletWithCoils
            position={[dhcX, 0, 28 + gi * 1.8]}
            color={COLOR_HEX[group.color] || '#555'}
            coilCount={group.totalCoils}
          />
          <Html position={[dhcX, 1.2, 28 + gi * 1.8]} center>
            <div style={{
              background: 'rgba(10,15,25,0.92)', color: '#FFF', padding: '4px 8px',
              borderRadius: 4, fontSize: 10, fontFamily: 'monospace',
              border: '1px solid #F59E0B60', userSelect: 'none', pointerEvents: 'none',
              whiteSpace: 'nowrap'
            }}>
              <div style={{ fontWeight: 700 }}>{group.product}</div>
              <div>{Math.round(group.totalLength)}m • {group.totalCoils} coils</div>
              {group.coilLength > 0 && <div style={{ color: '#AAA' }}>@ {group.coilLength}m/coil</div>}
              <div style={{ color: '#F59E0B', fontWeight: 700, marginTop: 2 }}>UNPACKED</div>
            </div>
          </Html>
        </group>
      ))}
    </group>
  );
}

// ─── HALL 4 PRODUCTION BOBINS (next phase = armouring) ─────
// Hall 4: X=11 to 35, Z=47.3 to 82.3
// L70 at WX=33 | Taping at LX=13.5
// Tokens whose next phase involves armouring/taping

export function Hall4ProductionBobins({ onSelectGroup, focusToken, onFocusLocated }) {
  const { data } = useBobinsState();

  const groups = useMemo(() => {
    if (!data?.tokens) return [];
    // Tokens whose nextPhaseName is armouring
    const tokens = data.tokens.filter(t => {
      if (t.isPhantom) return false;
      return t.nextPhaseName === ARMOURING_PHASE;
    });
    const map = {};
    for (const t of tokens) {
      const key = `${t.jobId}::armouring`;
      if (!map[key]) map[key] = { product: t.productLabel, phase: 'armouring', jobId: t.jobId, tokens: [] };
      map[key].tokens.push(t);
    }
    return Object.values(map);
  }, [data]);

  const DZ = 1.5;
  const DX = 1.5;
  const startX = 14;   // near left wall (door side)
  const startZ = 72;   // near Pay-off 1600 (Z=70), going toward back wall
  const slotsPerCol = Math.floor((82 - startZ) / DZ); // up to Z≈82 (back wall)

  return (
    <group>
      {(() => {
        let slotIdx = 0;
        return groups.map((group, gi) => {
          // A product whose EVERY drawn drum is shared has no card of its own to
          // show: the orange merge card on the drum is its label, and naming the
          // product twice at one spot is what put a card over a card. When the
          // group does keep its label, the merge card lifts above it so the two
          // stack instead of colliding.
          const allDrawnShared = group.tokens.length > 0 &&
            group.tokens.every(t => t.sharedWith?.length > 0);
          const showGroupLabel = group.tokens.length > 0 && !allDrawnShared;
          const mergeLift = showGroupLabel ? 3.5 : 2.2;
          const startSlot = slotIdx;
          const elems = group.tokens.map((tok, ti) => {
            const idx = startSlot + ti;
            const row = idx % slotsPerCol;
            const col = Math.floor(idx / slotsPerCol);
            const x = startX + col * DX;
            const z = startZ + row * DZ;
            const focus = isFocusTok(tok, focusToken);
            return (
              <group key={tok.tokenId}>
                <Drum3D position={[x, 0, z]} color={tok.color} phase={tok.phase} material={tok.drum_material} consuming={tok.status === 'CONSUMING'} active={tok.status === 'ACTIVE'} />
                {isPhantomToken(tok) && <PhantomMarker position={[x, 0, z]} />}
                {tok.sharedWith?.length > 0 && (
                  <MergeCard position={[x, 0, z]} partners={tok.sharedWith} shares={tok.mergeShares}
                    group={tok.mergeGroup} onClickGroup={onSelectGroup} lift={mergeLift} />
                )}
                {focus && <FocusBeacon position={[x, 0, z]} />}
                {focus && <FocusReporter position={[x, 0, z]} group={group} onLocated={onFocusLocated} />}
              </group>
            );
          });
          const firstRow = startSlot % slotsPerCol;
          const firstCol = Math.floor(startSlot / slotsPerCol);
          slotIdx += group.tokens.length;
          return (
            <group key={`h4-g-${gi}`}>
              {showGroupLabel && (
                <GroupLabel
                  position={[startX + firstCol * DX, 2.2, startZ + firstRow * DZ]}
                  group={group}
                  onClickGroup={onSelectGroup}
                />
              )}
              {elems}
            </group>
          );
        });
      })()}
    </group>
  );
}
